// Public API barrel for @cogo/kg-browser.
//
// Phase 2a Step 3 — browser runtime wrapper around Oxigraph WASM.
// See docs/agentic/architecture/wasm-client-distribution.md for the full
// design + budget targets.

export { initKgCore } from "./core-loader"
export type {
  InitKgCoreOpts,
  CoreCapabilities,
  InitResult,
} from "./core-loader"

export {
  detectCapabilities,
  detectSAB,
  detectWasmSimd,
  detectWasmBulkMemory,
  getStore,
  getLastCapabilities,
  getShapesTtl,
  isReady,
  _resetForTest,
} from "./core-loader"

export { applyShacl, validateCogoBlock } from "./shacl-runner"
export type {
  CogoBlockValidationDiagnostic,
  CogoBlockValidationResult,
  DiscoveredRule,
  ShaclQueryRule,
  ShaclReport,
  ShaclViolation,
} from "./shacl-runner"

export { serializeCogoJsonToTurtle } from "./serialize"

export { admitDesignCompositionOntology } from "./design-composition-admission"
export type {
  DesignCompositionAdmissionIssue,
  DesignCompositionAdmissionResult,
  DesignCompositionQuad,
  DesignCompositionRdfInputIssue,
  DesignCompositionReleaseCardinalityIssue,
  DesignCompositionShaclIssue,
  DuplicateDesignCompositionRegionIdIssue,
} from "./design-composition-admission"

export {
  admitDesignBindingOntology,
  DESIGN_BINDING_GAP_CODES,
} from "./design-binding-admission"
export type {
  DesignBindingAdmissionIssue,
  DesignBindingAdmissionResult,
  DesignBindingCardinalityIssue,
  DesignBindingGapCode,
  DesignBindingGapVocabularyIssue,
  DesignBindingQuad,
  DesignBindingRdfInputIssue,
  DesignBindingRefMismatchIssue,
  DesignBindingReleaseUnresolvableIssue,
  DesignBindingShaclIssue,
  DesignBindingTokenNotAdmittedIssue,
  DesignBindingTokenNotCanonicalIssue,
} from "./design-binding-admission"

export {
  admitUxApplicationLibraryOntology,
  UX_APPLICATION_LIBRARY_RDF_INPUT_BUDGETS,
} from "./ux-application-library-admission"
export type {
  UxApplicationLibraryAdmissionIssue,
  UxApplicationLibraryAdmissionIssueCode,
  UxApplicationLibraryAdmissionResult,
} from "./ux-application-library-admission"

export {
  loadJsonLdIntoNamedGraph,
  dropNamedGraph,
  countTriplesInGraph,
} from "./graph-scope"
export type { LoadJsonLdResult } from "./graph-scope"

export { fetchShard, invalidateShard } from "./shard-cache"
export type { ShardFetchOpts, ShardFetchResult } from "./shard-cache"

export { subscribeOntologyChanges } from "./realtime-subscriber"
export type {
  OntologyChangePayload,
  SubscribeOpts,
  Unsubscribe,
} from "./realtime-subscriber"

// Phase 440 Plan 04 RUNTIME-04 — Realtime invalidator (Branch A wrapper over
// Phase 439's subscribeOntologyChanges). See realtime-invalidator.ts header
// for the branch-decision audit trail.
export {
  subscribeToTenantOntology,
  NOOP_REALTIME_INVALIDATOR,
} from "./realtime-invalidator"
export type {
  RealtimeInvalidatorOpts,
  Subscription,
  IRealtimeInvalidator,
} from "./realtime-invalidator"

export { versionInfo, KG_BROWSER_CORE_VERSION, OXIGRAPH_VERSION } from "./version"
export type { KgBrowserVersion } from "./version"

import { getStore } from "./core-loader"

/**
 * Convenience wrapper around store.query for SELECT queries that bind a
 * single shape (Event, Action, Capability) and return name strings.
 *
 * Use this for simple lookups like "what events does this component emit?".
 * For complex queries with joins, fall back to `getStore().query()` directly.
 */
export interface QueryShapeResult {
  rows: Array<Record<string, string>>
  count: number
  durationMs: number
}

export function queryShape(sparql: string): QueryShapeResult {
  const store = getStore()
  if (!store) {
    throw new Error("[cogo-kg-browser] queryShape called before initKgCore")
  }
  const t0 = nowMs()
  const out = store.query(sparql)
  const rows: Array<Record<string, string>> = []
  if (Array.isArray(out)) {
    for (const row of out as Array<unknown>) rows.push(stringifyBinding(row))
  } else if (out && typeof (out as any)[Symbol.iterator] === "function") {
    for (const row of out as Iterable<unknown>) rows.push(stringifyBinding(row))
  }
  return { rows, count: rows.length, durationMs: nowMs() - t0 }
}

function stringifyBinding(row: unknown): Record<string, string> {
  const out: Record<string, string> = {}
  const r = row as Iterable<[string, { value: string } | null]>
  if (r && typeof (r as any)[Symbol.iterator] === "function") {
    for (const [k, v] of r as Iterable<[string, { value?: string } | null]>) {
      out[k] = v?.value ?? "null"
    }
  }
  return out
}

function nowMs(): number {
  const perf = (globalThis as any).performance
  if (perf && typeof perf.now === "function") return perf.now()
  return Date.now()
}

// ---------------------------------------------------------------------------
// Phase 0431 D-C6 + D-C7 — /version polling timer
//
// The browser polls the worker's /version endpoint on a 60-second timer.
// On detected lastMigration change vs the value captured at the previous
// tick, the polling tick invokes the optional `opts.onChange` callback so
// hosts that still depend on /version-derived signals can observe them.
//
// History (Phase 440 Plan 04 RUNTIME-04 — 2026-05-11):
//   The original D-C6 design ALSO dispatched a `BroadcastChannel(
//   "cogo-kg-invalidate")` message on each detected change so same-origin
//   tabs could invalidate caches without a server round-trip. That emitter
//   has been REMOVED because Phase 0439 REALTIME-01 (shipped at cogo-platform
//   commit `8a4482c1`) supersedes the cross-tab broadcast pattern with a
//   proper Supabase Realtime subscription on `cogo.application_ontology`.
//   Cache invalidation now flows through Phase 0440 RUNTIME-04 via
//   `subscribeToTenantOntology` (see ./realtime-invalidator.ts) — exposed to
//   higher-level callers via `LocalWasmRepository.subscribeToTenantInvalidation`
//   in `@cogo/kg-repository`.
//
//   The /version endpoint itself stays (some clients still poll for unrelated
//   reasons; D-D2 deprecates the broadcast specifically, not the endpoint).
//   The polling tick continues to fire `opts.onChange` so callers that have
//   their own change-detection wiring (e.g. soft-cache eviction, refetch
//   triggers) keep working.
//
// Workers cannot dispatch BroadcastChannel cross-origin to browser tabs
// (D-C6 rationale) — the browser must initiate. This module is the
// canonical /version-poll initiator. registerVersionPollingTimer is called
// automatically by initKgCore when the SDK boots; consumers usually don't
// call it directly. Override the poll interval via COGO_KG_POLL_INTERVAL_MS
// build-time env var or VersionPollingOpts.pollIntervalMs.
// ---------------------------------------------------------------------------

let pollingTimer: ReturnType<typeof setInterval> | null = null
let lastObservedMigration = ""
let lastObservedOntologyVersion = ""

/** Phase 0431 D-C7 LOCKED: production default; override via COGO_KG_POLL_INTERVAL_MS env. */
export const DEFAULT_POLL_INTERVAL_MS = 60_000

export interface VersionPollingOpts {
  /** Worker base URL — defaults to "" (relative; assumes same-origin worker). */
  baseUrl?: string
  /**
   * Override poll interval. Defaults to env COGO_KG_POLL_INTERVAL_MS or
   * DEFAULT_POLL_INTERVAL_MS (60_000ms).
   */
  pollIntervalMs?: number
  /** Optional callback fired BEFORE the BroadcastChannel postMessage. */
  onChange?: (msg: { newVersion: string; oldVersion: string; lastMigration: string }) => void
  /** Test seam — fetch implementation override (defaults to global fetch). */
  fetchImpl?: typeof fetch
}

function readPollIntervalEnv(): number | undefined {
  // Read COGO_KG_POLL_INTERVAL_MS from any of: process.env (Node/Vite SSR),
  // import.meta.env via globalThis, etc. We avoid `import.meta.env` directly
  // because it's not a stable TS type across all consumer toolchains; the
  // process.env path covers Vite (`define`-injected) and Node test runs.
  const raw = (globalThis as any)?.process?.env?.COGO_KG_POLL_INTERVAL_MS as
    | string
    | undefined
  if (typeof raw !== "string" || raw.length === 0) return undefined
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : undefined
}

/**
 * Phase 0431 D-C6: register a /version polling timer.
 *
 * Behavior:
 *   - First poll captures the current lastMigration as a silent baseline
 *     (no BroadcastChannel dispatch).
 *   - Subsequent polls compare against the baseline; on change, dispatches
 *     `BroadcastChannel("cogo-kg-invalidate").postMessage({newVersion,
 *     oldVersion, lastMigration})` and updates the baseline.
 *   - First poll fires after the first interval tick (no immediate fire on
 *     bootstrap — gives the SDK time to render before the network call).
 *   - Idempotent — calling twice is a no-op (existing timer is preserved).
 *
 * Teardown:
 *   - Listens for `beforeunload` and clears the interval.
 *   - Returns the timer as a side-effect; consumers can call
 *     `clearVersionPollingTimer()` for explicit teardown (test-friendly).
 */
export function registerVersionPollingTimer(opts: VersionPollingOpts = {}): void {
  if (pollingTimer !== null) return // already registered
  const envOverride = readPollIntervalEnv()
  const intervalMs =
    typeof opts.pollIntervalMs === "number" && opts.pollIntervalMs > 0
      ? opts.pollIntervalMs
      : envOverride ?? DEFAULT_POLL_INTERVAL_MS
  const baseUrl = opts.baseUrl ?? ""
  const fetchImpl = opts.fetchImpl ?? fetch

  const tick = async (): Promise<void> => {
    try {
      const res = await fetchImpl(`${baseUrl}/version`, { cache: "no-cache" })
      if (!res.ok) return
      const body = (await res.json()) as {
        lastMigration?: string
        ontologyVersion?: string
      }
      const current = body.lastMigration ?? ""
      const currentOntologyVersion = body.ontologyVersion ?? ""
      if (lastObservedMigration === "") {
        // First poll — capture baseline silently, do NOT broadcast.
        lastObservedMigration = current
        lastObservedOntologyVersion = currentOntologyVersion
        return
      }
      if (current && current !== lastObservedMigration) {
        const msg = {
          newVersion: currentOntologyVersion,
          oldVersion: lastObservedOntologyVersion,
          lastMigration: current,
        }
        try {
          opts.onChange?.(msg)
        } catch {
          // Caller-supplied callback errored — non-fatal; ignore.
        }
        // Phase 0440 Plan 04 RUNTIME-04 (2026-05-11) — REMOVED:
        // The original D-C6 design dispatched a postMessage on the
        // "cogo-kg-invalidate" BroadcastChannel here so same-origin tabs
        // could invalidate caches without a server round-trip. That
        // cross-tab fanout has been replaced by
        // Phase 0439 REALTIME-01 Supabase Realtime subscription on
        // `cogo.application_ontology`. Callers wanting cache invalidation
        // should subscribe via
        //   `subscribeToTenantOntology` (./realtime-invalidator.ts)
        // or, at the repository layer, via
        //   `LocalWasmRepository.subscribeToTenantInvalidation`
        // (@cogo/kg-repository). The polling tick keeps invoking the
        // optional `opts.onChange` callback above for backward compat.
        lastObservedMigration = current
        lastObservedOntologyVersion = currentOntologyVersion
      }
    } catch {
      /* Network error — retry next tick */
    }
  }

  pollingTimer = setInterval(tick, intervalMs)
  // Best-effort teardown when the host page unloads.
  try {
    const g = globalThis as unknown as { addEventListener?: (
      type: string,
      listener: () => void,
    ) => void }
    if (typeof g.addEventListener === "function") {
      g.addEventListener("beforeunload", clearVersionPollingTimer)
    }
  } catch {
    /* SSR / non-browser runtime — ignore */
  }
}

/** Phase 0431 D-C6: stop the polling timer; resets baseline. */
export function clearVersionPollingTimer(): void {
  if (pollingTimer !== null) {
    clearInterval(pollingTimer)
    pollingTimer = null
    lastObservedMigration = ""
    lastObservedOntologyVersion = ""
  }
}

export {
  ONTOLOGY_SHARD_REGISTRY,
  getOntologyShardsForConsumer,
  type OntologyShardConsumer,
  type OntologyShardEnforcement,
  type OntologyShardPublicationPolicy,
  type OntologyShardRegistry,
  type OntologyShardRegistryEntry,
  type OntologyShardRole,
  type OntologyShardScope,
  type OntologyShardValidatorLoad,
} from "./ontology-shard-registry"

export {
  validateDocumentOntologyContracts,
  type DocumentOntologyContractViolation,
} from "./document-ontology-contract-validator"
